import React, { useState, useEffect, useRef, useMemo } from 'react';
import Hint from './Hint';
import { findPriceByName, normalizeIngredientName } from '../utils/ingredientMatch';
import { useLang } from '../i18n/LangContext';
import BiName from '../i18n/BiName';
import { saveManualTranslation } from '../utils/translate';
import { searchVariants, mistypedKorean } from '../utils/hangulKeys';   // V122
import { makeRecipeCopy, hasSameName } from '../utils/recipeCopy';   // V150 레시피 복사
import { semiNameProblem, suggestSemiName, josa } from '../utils/semiClash';   // V153 반제품·식재료 이름 겹침
import { usableG, lossG } from '../utils/priceYield';   // V152 실사용량 (신선식품 로스 제외)

// V150: copyFrom — 이 레시피를 복사해서 새 레시피(새 버전)로. 저장하기 전엔 아무것도 생기지 않는다
function RecipeEditor({ item, copyFrom = null, categories, prices = [], onSave, onDelete, onClose, terms = {} }) {
  const T = {
    recipeItem: '레시피', category: '카테고리',
    ingredient: '식재료', servingBaseLabel: '기준량 (g)',
    totalOutputLabel: '총 생산량 (g)', lossRateEnabled: false,
    typeOptions: [{ value: 'semi', label: '반제품' }, { value: 'menu', label: '판매 메뉴' }],
    editorNewTitle: '새 레시피 추가', editorEditTitle: '레시피 편집',
    editorNamePlaceholder: '예: 떡볶이 소스',
    editorIngredientPlaceholder: '재료명 입력 (단가표 자동검색)',
    qtyUnit: 'g',
    ...terms,
  };
  const isNew = !item;
  const { t, unit, lang, fmt: money } = useLang();
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);   // V150
  // V150: 복사 — 처음 열 때 한 번만 초안을 만든다 (이름 'ver N', 재료·양·메모 그대로)
  const isCopy = !item && !!copyFrom;
  const allRecipes = useMemo(() => (categories || []).flatMap(c => c.items || []), [categories]);
  const [copyBase] = useState(() => (isCopy ? makeRecipeCopy(copyFrom, (categories || []).flatMap(c => c.items || []), prices) : null));
  const src = item || copyBase;   // 입력칸 처음 값
  // V114: 1g당 단가처럼 소수가 필요한 작은 금액 (원 / ¥)
  const moneySmall = (n) => {
    const v = Number(n) || 0;
    // 위안은 1g당 0.006처럼 아주 작아서 소수 둘째 자리로 자르면 0.01이 된다 → 작은 값은 유효숫자 2자리
    const num = v >= 10 ? v.toFixed(1) : v >= 0.1 ? v.toFixed(2) : String(Number(v.toPrecision(2)));
    return unit === '¥' ? `¥${num}` : `${num}원`;
  };
  // V114: 카테고리는 데이터라 한·중을 같이 보여준다 (보는 언어를 앞에)
  const catLabel = (c) => {
    const zh = String(c?.name_zh || '').trim();
    if (!zh) return c.name;
    return lang === 'zh' ? `${zh} · ${c.name}` : `${c.name} · ${zh}`;
  };

  const [name, setName] = useState(src?.name || '');
  const [type, setType] = useState(src?.type || 'semi');
  const [categoryName, setCategoryName] = useState('');
  // V115: 새 카테고리 직접 입력. 레시피를 지우다 마지막 레시피까지 지우면 카테고리도 같이 사라져서
  // (빈 카테고리는 삭제 시 정리됨) 선택지가 0개가 되고 새 레시피를 아예 못 만들던 문제.
  const NEW_CAT = '__new__';
  const [newCatName, setNewCatName] = useState('');
  const hasCats = (categories || []).length > 0;
  const isNewCat = !hasCats || categoryName === NEW_CAT;
  const [totalOutput, setTotalOutput] = useState(src?.total_output || '');
  const [servingG, setServingG] = useState(src?.serving_g || '');
  const [note, setNote] = useState(src?.note || '');
  // V109: 중국어 — 비워두면 저장 뒤 자동 번역. 직접 고치면 그 값이 우선(이후 AI가 안 덮음)
  const [nameZh, setNameZh] = useState(src?.name_zh || '');
  const [noteZh, setNoteZh] = useState(src?.note_zh || '');
  const [sellingPrice, setSellingPrice] = useState(src?.selling_price || '');
  const [ingredients, setIngredients] = useState(src?.ingredients || [{ name: '', qty: '' }]);
  const [suggestions, setSuggestions] = useState({ idx: null, list: [] });
  const suggRef = useRef(null);

  // V115: 카테고리 초기값은 '이 레시피를 처음 열 때' 한 번만 정한다.
  // 예전엔 categories가 바뀔 때마다(뒤에서 번역이 채워질 때 등) 첫 카테고리로 되돌아가
  // 사용자가 고른 카테고리·'새 카테고리'가 풀려버렸다. 고른 값이 아직 유효하면 그대로 둔다.
  const catNameRef = useRef(categoryName);
  catNameRef.current = categoryName;
  const catInitFor = useRef(null);
  useEffect(() => {
    const key = item?.id || (copyFrom ? `copy:${copyFrom.id}` : '(new)');
    const cur = catNameRef.current;
    const list = categories || [];
    const stillValid = cur === NEW_CAT || list.some(c => c.name === cur);
    if (catInitFor.current === key && stillValid) return;
    catInitFor.current = key;
    const ref = item || copyFrom;   // V150: 복사본은 원본과 같은 카테고리로 시작
    const own = ref ? list.find(c => (c.items || []).some(i => i.id === ref.id)) : null;
    setCategoryName(own ? own.name : list.length > 0 ? list[0].name : NEW_CAT);
  }, [item, copyFrom, categories]);

  const addIngredient = () => {
    setIngredients([...ingredients, { name: '', qty: '' }]);
  };

  const removeIngredient = (idx) => {
    setIngredients(ingredients.filter((_, i) => i !== idx));
  };

  // ── V130: 배수 조정 ───────────────────────────────────────────────────────
  // 레시피 전체를 N배로 — 모든 식재료 수량과 총 생산량을 곱한다. 1인분(g)은 그대로 (1인분 크기는 안 바뀜).
  // 바로 적용 버튼(×½ ×2 ×10, 직접 입력 + 적용)으로 숫자를 미리 보고 저장. 입력만 하고 저장해도 그 배수로 저장된다.
  const [scaleInput, setScaleInput] = useState('');
  const [scaleBase, setScaleBase] = useState(null);   // 처음 배수를 적용하기 전 값 { ingredients, totalOutput }
  const [scaleTotal, setScaleTotal] = useState(1);    // 지금까지 곱한 배수 (원래의 몇 배인지)
  const scaleNum = (v, f) => {
    const n = Number(v);
    if (v === '' || v == null || !Number.isFinite(n) || n <= 0) return v;   // 빈 칸은 그대로
    return Math.round(n * f * 100) / 100;
  };
  const scaledValues = (f) => ({
    ings: ingredients.map(i => ({ ...i, qty: scaleNum(i.qty, f) })),
    out: scaleNum(totalOutput, f),
  });
  const applyScale = (f) => {
    const k = Number(f);
    if (!Number.isFinite(k) || k <= 0 || k === 1) return;
    if (!scaleBase) setScaleBase({ ingredients, totalOutput });
    const { ings, out } = scaledValues(k);
    setIngredients(ings);
    setTotalOutput(out);
    setScaleTotal(prev => Math.round(prev * k * 10000) / 10000);
    setScaleInput('');
    setSuggestions({ idx: null, list: [] });
  };
  const undoScale = () => {
    if (!scaleBase) return;
    setIngredients(scaleBase.ingredients);
    setTotalOutput(scaleBase.totalOutput);
    setScaleBase(null);
    setScaleTotal(1);
    setScaleInput('');
  };
  const fmtFactor = (k) => (k === 0.5 ? '½' : String(Math.round(k * 100) / 100));

  // ── V129: 식재료 순서 바꾸기 ─────────────────────────────────────────────
  // 왼쪽 ⠿ 손잡이를 끌어서 놓는다. 마우스·터치 모두 (포인터 이벤트). 손잡이에 초점이 있으면 ↑↓ 키로도.
  const rowRefs = useRef([]);
  const [drag, setDrag] = useState(null);   // { from, over } — 끄는 중인 줄과 놓일 자리
  const moveIngredient = (from, to) => {
    const n = ingredients.length;
    if (from === to || from < 0 || to < 0 || from >= n || to >= n) return;
    const next = [...ingredients];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setIngredients(next);
    setSuggestions({ idx: null, list: [] });
  };
  // 손가락(포인터) 높이로 놓일 자리 계산 — 각 줄의 가운데보다 위면 그 줄 앞
  const dropIndexAt = (y, from) => {
    const n = ingredients.length;
    let before = n;
    for (let i = 0; i < n; i++) {
      const el = rowRefs.current[i];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      if (y < r.top + r.height / 2) { before = i; break; }
    }
    return before > from ? before - 1 : before;
  };
  const onHandleDown = (e, idx) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) { /* 오래된 브라우저 */ }
    setDrag({ from: idx, over: idx });
  };
  const onHandleMove = (e) => {
    if (!drag) return;
    const over = dropIndexAt(e.clientY, drag.from);
    if (over !== drag.over) setDrag({ ...drag, over });
  };
  const onHandleUp = () => {
    if (!drag) return;
    moveIngredient(drag.from, drag.over);
    setDrag(null);
  };
  const onHandleKey = (e, idx) => {
    if (e.key === 'ArrowUp' && idx > 0) { e.preventDefault(); moveIngredient(idx, idx - 1); }
    if (e.key === 'ArrowDown' && idx < ingredients.length - 1) { e.preventDefault(); moveIngredient(idx, idx + 1); }
  };
  const dragRowStyle = (idx) => {
    if (!drag) return null;
    if (drag.from === idx) return { opacity: 0.45 };
    if (drag.over === idx && drag.over !== drag.from) {
      return { boxShadow: drag.over < drag.from ? '0 -3px 0 #EA580C' : '0 3px 0 #EA580C', borderRadius: 4 };
    }
    return null;
  };

  // V122: 재료명 자동완성 — 한/영을 안 바꾸고 친 것(rkswkd → 간장)과 중국어 이름으로도 찾는다
  const findIngredientSuggestions = (value) => {
    const qs = [...new Set(searchVariants(value).map(normalizeIngredientName).filter(Boolean))];
    if (!qs.length) return [];
    const self = type === 'semi' ? normalizeIngredientName(name) : '';   // V153: 반제품 자기 자신은 재료 후보에서 뺀다
    return prices.filter(p => {
      const ko = normalizeIngredientName(p.name);
      const zh = normalizeIngredientName(p.name_zh);
      if (self && ko === self && p.category === '반제품') return false;
      return qs.some(q => ko.includes(q) || (zh && zh.includes(q)));
    }).slice(0, 8);
  };
  const showSuggestions = (idx, value) => {
    const list = normalizeIngredientName(value) ? findIngredientSuggestions(value) : [];
    const ko = mistypedKorean(value);
    if (list.length || ko) setSuggestions({ idx, list, ko });
    else setSuggestions({ idx: null, list: [] });
  };
  // 영어 상태로 친 이름이 단가표 품목과 똑같으면, 칸을 벗어날 때 한글 이름으로 바꿔 둔다
  const fixMistypedName = (idx) => {
    setIngredients(prev => {
      const cur = prev[idx];
      const ko = cur && mistypedKorean(cur.name);
      if (!ko) return prev;
      const hit = findPriceByName(prices, ko);
      if (!hit) return prev;
      const next = [...prev];
      next[idx] = { ...cur, name: hit.name };
      return next;
    });
  };

  const updateIngredient = (idx, field, value) => {
    const updated = [...ingredients];
    updated[idx] = {
      ...updated[idx],
      [field]: (field === 'qty' || field === 'lossRate') ? Number(value) : value,
    };
    setIngredients(updated);

    // 재료명 입력 시 단가표에서 자동완성 후보 검색 (공백 무시, V122: 영타·중국어 이름도)
    if (field === 'name') showSuggestions(idx, value);
  };

  const selectSuggestion = (idx, priceName) => {
    const updated = [...ingredients];
    updated[idx] = { ...updated[idx], name: priceName };
    setIngredients(updated);
    setSuggestions({ idx: null, list: [] });
  };

  // V125: 단가가 정해진 품목인가 — 기준량이 있고 단가가 숫자(0 포함). 0원은 무료 재료(물 등)
  const priceKnown = (p) => !!p && Number(p.net_g) > 0 && p.price !== '' && p.price != null
    && Number.isFinite(Number(p.price)) && Number(p.price) >= 0;

  // ── 식재료 중량(g) + 원가 합계 (실시간) ──
  const ingredientSummary = useMemo(() => {
    const validList = ingredients.filter(i => (i.name || '').trim() && Number(i.qty) > 0);
    const totalWeight = validList.reduce((s, ing) => s + (Number(ing.qty) || 0), 0);
    const totalCost = validList.reduce((s, ing) => {
      const p = findPriceByName(prices, ing.name);
      if (!p || !usableG(p) || !Number(p.price)) return s;
      return s + (Number(ing.qty) || 0) / usableG(p) * Number(p.price);   // V152 실사용량 기준
    }, 0);
    const matchedCount = validList.filter(ing => priceKnown(findPriceByName(prices, ing.name))).length;
    const tOut = Number(totalOutput) || 0;
    const diffPct = tOut > 0 ? Math.round(((totalWeight - tOut) / tOut) * 100) : null;
    return {
      count: validList.length,
      matchedCount,
      totalWeight: Math.round(totalWeight * 10) / 10,
      totalCost: Math.round(totalCost * 100) / 100,   // V114: 위안 소수 유지 — 표시는 money()
      tOut,
      diffPct,
    };
  }, [ingredients, totalOutput, prices]);
  const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

  const handleSave = () => {
    if (!name.trim()) { alert(t('레시피 이름을 입력해주세요')); return; }
    const finalCat = isNewCat ? newCatName.trim() : categoryName;
    if (!finalCat) { alert(t(isNewCat ? '새 카테고리 이름을 입력해주세요' : '카테고리를 선택해주세요')); return; }
    // V130: 배수 칸에 숫자만 넣고 '적용'을 안 눌렀어도 그 배수로 저장
    const pendingK = Number(scaleInput);
    const { ings: ingsToSave, out: outToSave } = (scaleInput !== '' && Number.isFinite(pendingK) && pendingK > 0 && pendingK !== 1)
      ? scaledValues(pendingK)
      : { ings: ingredients, out: totalOutput };
    const validIngredients = ingsToSave.filter(i => i.name.trim() && i.qty);
    if (validIngredients.length === 0) { alert(t('재료를 최소 1개 이상 입력해주세요')); return; }
    // V153: 반제품 이름 겹침 막기 — 레시피·단가표는 이름으로 이어져서 같은 이름이면 하나로 섞인다
    if (type === 'semi') {
      // 예전에 겹친 반제품의 이름만 바꾸면 재료 안의 같은 이름까지 같이 바뀌어 또 겹친다 → 레시피 화면의 고치기로
      const wasSelfRef = item && item.type === 'semi' && (item.ingredients || []).some(i => normalizeIngredientName(i && i.name) === normalizeIngredientName(item.name));
      const nowSelfRef = validIngredients.some(i => normalizeIngredientName(i && i.name) === normalizeIngredientName(name));
      if (wasSelfRef && (nowSelfRef || normalizeIngredientName(name) !== normalizeIngredientName(item.name))) {
        alert(Z(`이 반제품은 재료 ${josa(`'${item.name}'`, '과/와')} 이름이 겹쳐 있어요.\n레시피 화면의 '⚠ 이름 바꾸고 고치기'로 고쳐 주세요 (재료 단가까지 같이 정리돼요).`,
                `这个半成品和食材「${item.name}」重名。\n请用配方页面上的「⚠ 改名并修好」来改（食材单价也会一起整理）。`));
        return;
      }
      const prob = semiNameProblem(name, { recipeId: item?.id || null, ingredients: validIngredients, allRecipes, prices });
      if (prob) {
        const sug = suggestSemiName(name, allRecipes, prices);
        alert(prob === 'raw'
          ? Z(`단가표에 같은 이름의 식재료 ${josa(`'${name.trim()}'`, '이/가')} 있어요.\n반제품은 다른 이름으로 지어 주세요 (예: ${sug}).\n이름이 같으면 반제품과 식재료가 하나로 섞여요.`,
              `单价表里已经有同名的食材「${name.trim()}」。\n半成品请换个名字（例：${sug}）。\n名字一样的话，半成品和食材会混在一起。`)
          : prob === 'semi'
            ? Z(`같은 이름의 반제품이 이미 있어요. 이름을 바꿔 주세요 (예: ${sug}).`, `已经有同名的半成品，请改个名字（例：${sug}）。`)
            : Z(`재료에 이 반제품과 같은 이름 ${josa(`'${name.trim()}'`, '이/가')} 있어요.\n반제품 이름을 바꿔 주세요 (예: ${sug}).`,
                `食材里有和这个半成品同名的「${name.trim()}」。\n请给半成品换个名字（例：${sug}）。`));
        return;
      }
    }
    // V150: 복사본은 다른 레시피와 이름이 같으면 안 된다 (반제품은 이름으로 연결돼서 원본과 섞인다)
    if (isCopy && hasSameName(name, allRecipes)) {
      alert(Z('같은 이름의 레시피가 이미 있어요. 이름을 바꿔 주세요.', '已经有同名的配方，请改个名字。'));
      return;
    }

    // V109: 한국어를 고쳤는데 중국어 칸은 손대지 않았다면 옛 번역은 틀린 번역이 된다.
    // 그럴 땐 비워서 저장 뒤 다시 번역받는다.
    // V150: 복사본은 복사할 때 채운 값(이름 ver N·중국어 ver N)을 기준으로 본다
    const base = item || copyBase;
    const origNote = String(base?.note || '').trim();
    const origNameZh = String(base?.name_zh || '').trim();
    const origNoteZh = String(base?.note_zh || '').trim();
    let zhName = nameZh.trim();
    let zhNote = noteZh.trim();
    // V164: 사장님 "한국어나 중국어든 직접 채워 넣으면 둘 중 무엇을 고쳐도 다른 언어 쪽은 그대로" — 이름은 중국어를 그대로 둔다
    //       (비어 있을 때만 저장 뒤 자동 번역). 조리 메모는 내용이 바뀌는 것이라 예전처럼 다시 번역
    if (note.trim() !== origNote && zhNote === origNoteZh) zhNote = '';
    if (!note.trim()) zhNote = '';
    // 사람이 직접 넣은 중국어는 공용 캐시에 '수동'으로 잠근다 → AI가 다시 덮지 않음
    if (zhName && zhName !== origNameZh) saveManualTranslation(name.trim(), zhName, 'menu').catch(() => {});
    if (zhNote && zhNote !== origNoteZh) saveManualTranslation(note.trim(), zhNote, 'recipe').catch(() => {});

    const savedItem = {
      id: item?.id || 'R' + Date.now(),
      name: name.trim(),
      type,
      total_output: Number(outToSave) || 0,
      total_input: Number(outToSave) || 0,
      serving_g: Number(servingG) || 0,
      note: note.trim(),
      name_zh: zhName,
      note_zh: zhNote,
      selling_price: Number(sellingPrice) || 0,
      ingredients: validIngredients,
      recipeCode: item?.recipeCode || 'R' + Date.now(),
    };
    // V150: 어느 레시피에서 복사했는지 (버전 묶음) — 고쳐 저장해도 유지
    const copiedFrom = item ? item.copiedFrom : copyBase?.copiedFrom;
    if (copiedFrom && copiedFrom.id) savedItem.copiedFrom = { id: copiedFrom.id, name: String(copiedFrom.name || '') };
    if (!T.lossRateEnabled) {
      // 식음료 모드: lossRate 필드 제거 (기존 호환성)
      savedItem.ingredients = savedItem.ingredients.map(({ lossRate, unit, ...rest }) => rest);
    }
    onSave(savedItem, finalCat);
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: '8px', fontSize: '14px', outline: 'none',
  };

  const labelStyle = {
    fontSize: '12px', fontWeight: '700', color: '#6b7280',
    marginBottom: '6px', display: 'block',
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff', borderRadius: '16px', width: '600px', maxWidth: '95vw',
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* 헤더 */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '800', color: '#18181B' }}>
            {isCopy ? `📋 ${Z('레시피 복사 — 새 버전', '复制配方 — 新版本')}` : t(isNew ? T.editorNewTitle : T.editorEditTitle)}
          </h2>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af' }}>
            ✕
          </button>
        </div>

        {/* 내용 */}
        <div style={{ padding: '24px' }}>

          {/* V150 복사 안내 */}
          {isCopy && (
            <div data-copy-notice style={{ background: '#FFF7ED', border: '1.5px solid #FDBA74', borderRadius: 10, padding: '10px 12px',
                                           marginBottom: 16, fontSize: 13, color: '#9A3412', lineHeight: 1.6 }}>
              <strong>{Z(`'${copyFrom.name}'을(를) 복사했어요.`, `已复制「${copyFrom.name_zh || copyFrom.name}」。`)}</strong>{' '}
              {Z('바꿀 부분만 고치고 저장하면 새 레시피로 생겨요. 원본은 그대로예요.', '只改要改的地方，保存后会变成一个新配方，原配方不变。')}
              {copyFrom.type === 'semi' && (
                <div style={{ fontSize: 12, color: '#B45309', marginTop: 2 }}>
                  {Z('이 반제품을 쓰는 메뉴는 계속 원본을 써요. 새 버전을 쓰려면 메뉴의 재료 이름을 바꿔 주세요.',
                     '用到这个半成品的菜品还是用原配方。要用新版本，请把菜品里的食材名改成新名字。')}
                </div>
              )}
            </div>
          )}

          {/* 기본 정보 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>{t(T.recipeItem + ' 이름')} *</label>
              <input value={name} onChange={e => setName(e.target.value)}
                placeholder={t(T.editorNamePlaceholder)} style={inputStyle} />
              <input value={nameZh} onChange={e => setNameZh(e.target.value)}
                placeholder={`中文名 — ${t('비워두면 저장 후 자동 번역')}`}
                style={{ ...inputStyle, marginTop: 6, fontSize: '13px', color: '#1A56A0', borderStyle: 'dashed' }} />
            </div>

            <div>
              <label style={labelStyle}>{t('유형')} *</label>
              <select value={type} onChange={e => setType(e.target.value)}
                style={{ ...inputStyle, background: '#fff' }}>
                {T.typeOptions.map(o => (
                  <option key={o.value} value={o.value}>{t(o.label)}</option>
                ))}
              </select>
              {!T.lossRateEnabled && (
                <Hint hintKey="editor_type" arrow="top" mt={6} mb={0}
                  zh={<><strong>售卖菜品</strong>是直接卖给客人的菜，<strong>半成品</strong>是多个菜共用的基础材料（酱汁、高汤、小菜等）。先登记半成品，就能在其他配方里当食材调用。</>}>
                  <strong>판매 메뉴</strong>는 손님에게 직접 파는 메뉴,{' '}
                  <strong>반제품</strong>은 여러 메뉴에 쓰이는 공통 재료예요 (소스, 육수, 밑반찬 등).
                  반제품을 먼저 등록해두면 다른 레시피에서 재료로 불러올 수 있어요.
                </Hint>
              )}
              {T.lossRateEnabled && (
                <Hint hintKey="editor_type_interior" arrow="top" mt={6} mb={0}>
                  <strong>시공 항목</strong>은 견적에 포함되는 시공 유형,{' '}
                  <strong>부자재</strong>는 여러 시공에 공통으로 쓰이는 하위 자재예요.
                </Hint>
              )}
            </div>

            <div>
              <label style={labelStyle}>{t(T.category)} *</label>
              {hasCats && (
                <select value={categoryName} onChange={e => setCategoryName(e.target.value)}
                  style={{ ...inputStyle, background: '#fff' }}>
                  {categories.map(c => (
                    <option key={c.name} value={c.name}>{catLabel(c)}</option>
                  ))}
                  <option value={NEW_CAT}>{t('➕ 새 카테고리 만들기')}</option>
                </select>
              )}
              {isNewCat && (
                <input value={newCatName} onChange={e => setNewCatName(e.target.value)}
                  autoFocus={hasCats}
                  placeholder={t('새 카테고리 이름 (예: 소스 / 양념)')}
                  style={{ ...inputStyle, marginTop: hasCats ? 6 : 0, borderColor: '#FF8F00' }} />
              )}
              {!hasCats && (
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                  {t('아직 카테고리가 없어요. 이름을 입력하면 새로 만들어집니다.')}
                </div>
              )}
            </div>

            {/* 판매 메뉴일 때만 판매가격 표시 */}
            {type === 'menu' && (
              <div>
                <label style={labelStyle}>{t('판매가격')} ({unit}) *</label>
                <input type="number" value={sellingPrice} onChange={e => setSellingPrice(e.target.value)}
                  placeholder={unit === '¥' ? '例：38' : '예: 8000'} style={{ ...inputStyle, borderColor: '#FF8F00' }} />
              </div>
            )}

            {/* V109: 조리 메모 — 여러 줄. 주방 인쇄물에 그대로 찍힌다 */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={labelStyle}>{t('조리 메모')}</label>
              <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
                placeholder={lang === 'zh'
                  ? '制作步骤、保存方法、注意事项等\n例）1. 大葱斜切  2. 大火炒2分钟'
                  : '조리 순서, 보관 방법, 주의사항 등\n예) 1. 대파는 어슷썰기  2. 센불에 2분 볶기'}
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }} />
              <textarea value={noteZh} onChange={e => setNoteZh(e.target.value)} rows={2}
                placeholder={`中文 — ${t('비워두면 저장 후 자동 번역')}`}
                style={{ ...inputStyle, marginTop: 6, fontSize: '13px', color: '#1A56A0', borderStyle: 'dashed',
                         resize: 'vertical', lineHeight: 1.5, fontFamily: 'inherit' }} />
            </div>
          </div>

          {/* 재료 목록 */}
          <div>
            <Hint hintKey="editor_ingredients" mb={10}
              zh={T.lossRateEnabled ? undefined : <>输入食材名称会<strong>自动搜索单价表里登记的品项</strong>。单价表里没有的也可以直接输入。</>}>
              {T.ingredient}명을 입력하면 <strong>단가표에 등록된 항목이 자동 검색</strong>돼요.
              단가표에 없으면 직접 입력해도 됩니다.
            </Hint>
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>{t(T.ingredient + ' 목록')} *</label>
              {/* V130: 배수 조정 — 수량·총 생산량을 한 번에 N배로 */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#71717A', fontWeight: 700 }}>{t('배수')}</span>
                {[0.5, 2, 10].map(k => (
                  <button key={k} type="button" onClick={() => applyScale(k)}
                    style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid #E4E4E7', background: '#fff',
                             color: '#52525B', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    ×{fmtFactor(k)}
                  </button>
                ))}
                <span style={{ fontSize: 12, color: '#A1A1AA' }}>×</span>
                <input type="number" min="0" step="any" value={scaleInput}
                  onChange={e => setScaleInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyScale(scaleInput); } }}
                  placeholder={lang === 'zh' ? '例：3' : '예: 3'}
                  aria-label={t('배수')}
                  style={{ width: 64, padding: '5px 8px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, outline: 'none' }} />
                <button type="button" onClick={() => applyScale(scaleInput)}
                  disabled={!(Number(scaleInput) > 0 && Number(scaleInput) !== 1)}
                  style={{ padding: '5px 10px', borderRadius: 8, border: 'none',
                           background: Number(scaleInput) > 0 && Number(scaleInput) !== 1 ? '#EA580C' : '#F4F4F5',
                           color: Number(scaleInput) > 0 && Number(scaleInput) !== 1 ? '#fff' : '#A1A1AA',
                           fontSize: 12, fontWeight: 800, cursor: 'pointer' }}>
                  {t('적용')}
                </button>
              </div>
            </div>
            {(scaleBase || (Number(scaleInput) > 0 && Number(scaleInput) !== 1)) && (
              <div style={{ marginBottom: 10, padding: '8px 12px', borderRadius: 8, background: '#FFF7ED', border: '1px solid #FDBA74',
                            fontSize: 12, color: '#9A3412', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>
                  {scaleBase
                    ? (lang === 'zh'
                        ? `已调整为原来的 ×${fmtFactor(scaleTotal)} — 所有数量和总产出量已改变，按“保存”后生效`
                        : `원래 레시피의 ×${fmtFactor(scaleTotal)} — 모든 수량과 총 생산량이 바뀌었어요. 저장을 눌러야 반영돼요`)
                    : (lang === 'zh'
                        ? `按“保存”时将按 ×${fmtFactor(Number(scaleInput))} 保存（也可以先按“应用”预览）`
                        : `저장하면 ×${fmtFactor(Number(scaleInput))}로 저장돼요 (먼저 '적용'을 눌러 숫자를 확인할 수 있어요)`)}
                </span>
                {scaleBase && (
                  <button type="button" onClick={undoScale}
                    style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 6, border: '1px solid #FDBA74', background: '#fff',
                             color: '#9A3412', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                    {t('원래대로')}
                  </button>
                )}
              </div>
            )}

            {/* 테이블 헤더 */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: T.lossRateEnabled ? '22px 1fr 90px 80px 80px 40px' : '22px 1fr 120px 40px',   // V129 손잡이 칸
              gap: '8px', marginBottom: '8px'
            }}>
              <span></span>
              <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '600' }}>{t(T.ingredient + '명')}</span>
              <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '600' }}>
                {t('수량')} {T.lossRateEnabled ? '' : `(${T.qtyUnit})`}
              </span>
              {T.lossRateEnabled && (
                <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '600' }}>단위</span>
              )}
              {T.lossRateEnabled && (
                <span style={{ fontSize: '12px', color: '#A1A1AA', fontWeight: '600' }}>손실률%</span>
              )}
              <span></span>
            </div>

            {ingredients.map((ing, idx) => (
              <div key={idx} ref={el => { rowRefs.current[idx] = el; }}
                style={{ marginBottom: '8px', transition: 'box-shadow .1s', ...dragRowStyle(idx) }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: T.lossRateEnabled ? '22px 1fr 90px 80px 80px 40px' : '22px 1fr 120px 40px',
                  gap: '8px'
                }}>
                  {/* V129: 순서 바꾸기 손잡이 — 끌어서 놓기, 또는 초점 두고 ↑↓ */}
                  <span
                    role="button" tabIndex={0}
                    aria-label={t('끌어서 순서 바꾸기')} title={t('끌어서 순서 바꾸기')}
                    onPointerDown={e => onHandleDown(e, idx)}
                    onPointerMove={onHandleMove}
                    onPointerUp={onHandleUp}
                    onPointerCancel={() => setDrag(null)}
                    onKeyDown={e => onHandleKey(e, idx)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center', height: 36,
                      cursor: drag ? 'grabbing' : 'grab', touchAction: 'none', userSelect: 'none',
                      color: drag && drag.from === idx ? '#EA580C' : '#A1A1AA', fontSize: 18, lineHeight: 1,
                      borderRadius: 6,
                    }}>⠿</span>
                  {/* 재료명 + 자동완성 */}
                  <div style={{ position: 'relative' }}>
                    <input
                      value={ing.name}
                      onChange={e => updateIngredient(idx, 'name', e.target.value)}
                      onFocus={() => {
                        if (ing.name.trim()) showSuggestions(idx, ing.name);
                      }}
                      onBlur={() => {
                        fixMistypedName(idx);
                        setTimeout(() => setSuggestions({ idx: null, list: [] }), 150);
                      }}
                      placeholder={t(T.editorIngredientPlaceholder)}
                      style={{ ...inputStyle, padding: '8px 12px', width: '100%', boxSizing: 'border-box' }}
                    />
                    {suggestions.idx === idx && (suggestions.list.length > 0 || suggestions.ko) && (
                      <div ref={suggRef} style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                        background: '#fff', border: '1.5px solid #FF6F0F', borderRadius: '8px',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: '2px',
                        maxHeight: '200px', overflowY: 'auto',
                      }}>
                        {/* V122: 영어 상태로 친 글자 → 한글로 바꾸기 */}
                        {suggestions.ko && (
                          <div
                            onMouseDown={e => { e.preventDefault(); updateIngredient(idx, 'name', suggestions.ko); }}
                            style={{
                              padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                              borderBottom: '1px solid #f0f0f0', background: '#FFF7ED', color: '#9A3412',
                              display: 'flex', alignItems: 'center', gap: '6px',
                            }}>
                            <span>⌨ {t('한글로 바꾸기')}</span>
                            <strong style={{ color: '#18181B' }}>{suggestions.ko}</strong>
                          </div>
                        )}
                        {suggestions.list.map((p, i) => (
                          <div
                            key={i}
                            onMouseDown={() => selectSuggestion(idx, p.name)}
                            style={{
                              padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
                              borderBottom: '1px solid #f0f0f0',
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}
                          >
                            <span style={{ fontWeight: '600', color: '#18181B' }}><BiName ko={p.name} zh={p.name_zh} size={11} /></span>
                            <span style={{ fontSize: '11px', color: '#9ca3af', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                              {/* V153: 반제품인지 식재료인지 */}
                              {p.category && (
                                <span data-sugg-cat style={{ fontSize: 10, fontWeight: 700, borderRadius: 4, padding: '1px 5px',
                                  background: p.category === '반제품' ? '#dbeafe' : '#F4F4F5', color: p.category === '반제품' ? '#1e40af' : '#52525B' }}>
                                  {t(p.category)}
                                </span>
                              )}
                              {p.spec || ''} · {money(p.price)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 수량 */}
                  <input
                    type="number"
                    value={ing.qty}
                    onChange={e => updateIngredient(idx, 'qty', e.target.value)}
                    placeholder="0"
                    style={{ ...inputStyle, padding: '8px 12px' }}
                  />

                  {/* 단위 (인테리어 모드) */}
                  {T.lossRateEnabled && (
                    <input
                      value={ing.unit || ''}
                      onChange={e => updateIngredient(idx, 'unit', e.target.value)}
                      placeholder="m², kg, L"
                      style={{ ...inputStyle, padding: '8px 10px', fontSize: '13px' }}
                    />
                  )}

                  {/* 손실률 (인테리어 모드) */}
                  {T.lossRateEnabled && (
                    <input
                      type="number"
                      min="0" max="50"
                      value={ing.lossRate ?? ''}
                      onChange={e => updateIngredient(idx, 'lossRate', e.target.value)}
                      placeholder="0"
                      style={{ ...inputStyle, padding: '8px 10px', borderColor: '#fde68a' }}
                    />
                  )}

                  <button onClick={() => removeIngredient(idx)}
                    style={{
                      width: '36px', height: '36px', borderRadius: '8px',
                      border: '1px solid #E4E4E7', background: '#fff5f5',
                      color: '#52525B', cursor: 'pointer', fontSize: '16px',
                    }}>
                    ✕
                  </button>
                </div>

                {/* ── 단가표 매칭 표시 (이름 입력된 경우, 공백 무시) ── */}
                {(ing.name || '').trim() && (() => {
                  const p = findPriceByName(prices, ing.name);
                  if (priceKnown(p)) {
                    const perG = Number(p.price) / usableG(p);   // V152 실사용량 기준
                    const loss = lossG(p);
                    const free = Number(p.price) === 0;
                    const lineCost = Number(ing.qty) > 0 ? perG * Number(ing.qty) : null;   // V125: 반올림하지 않음 (위안 소수)
                    return (
                      <div style={{
                        marginTop: 5, padding: '5px 10px', fontSize: 11,
                        background: '#FAFAFA', borderRadius: 6,
                        display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
                        color: '#EA580C',
                      }}>
                        <span style={{ fontWeight: 700 }}>✓ {t('연동')}</span>
                        {p.name_zh && <span style={{ color: '#1A56A0', fontWeight: 600 }}>{p.name_zh}</span>}
                        {free ? (
                          <span style={{ color: '#16A34A', fontWeight: 700 }}>{t('무료')} · {money(0)}</span>
                        ) : (<>
                        <span style={{ color: '#EA580C' }}>
                          {p.spec ? `${p.spec} · ` : ''}{money(p.price)}
                          {p.net_g ? ` / ${usableG(p)}g` : ''}
                          {loss > 0 && <span data-ing-loss style={{ color: '#9ca3af', marginLeft: 4 }}>
                            ({lang === 'zh' ? `${p.net_g}g 去掉损耗 ${loss}g` : `${p.net_g}g에서 로스 ${loss}g 뺌`})
                          </span>}
                        </span>
                        <span style={{ color: '#6b7280' }}>
                          ({moneySmall(perG)}/g)
                        </span>
                        </>)}
                        {lineCost !== null && (
                          <span style={{ marginLeft: 'auto', color: '#18181B', fontWeight: 800 }}>
                            = {money(lineCost)}
                          </span>
                        )}
                      </div>
                    );
                  }
                  if (p) {   // V125: 단가표에 있지만 단가·기준량이 비었음 (0원은 위에서 무료로 처리)
                    return (
                      <div style={{
                        marginTop: 5, padding: '5px 10px', fontSize: 11,
                        background: '#F4F4F5', borderRadius: 6, color: '#52525B',
                      }}>
                        ⚠ {t('단가표에 항목은 있으나 가격/용량 미입력 — 단가표 화면에서 채워주세요')}
                      </div>
                    );
                  }
                  return (
                    <div style={{
                      marginTop: 5, padding: '5px 10px', fontSize: 11,
                      background: '#F4F4F5', borderRadius: 6, color: '#b91c1c',
                    }}>
                      ⚠ {t('단가표 미매칭 — 이름을 단가표 항목과 일치시키거나 단가표에 먼저 등록하세요')}
                    </div>
                  );
                })()}
              </div>
            ))}

            {/* ── 식재료 합계 박스 (실시간 중량 + 원가) ── */}
            {ingredientSummary.count > 0 && (
              <div style={{
                marginTop: 12, padding: '12px 14px',
                background: '#FAFAFA',
                borderRadius: 10, border: '1.5px solid #EA580C',
              }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  flexWrap: 'wrap', gap: 10,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#18181B' }}>
                    📐 {t('식재료 합계')}
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#EA580C', marginLeft: 6 }}>
                      ({ingredientSummary.count}{lang === 'zh' ? '种' : '종'}
                      {ingredientSummary.matchedCount < ingredientSummary.count
                        ? ` · ${t('단가 매칭')} ${ingredientSummary.matchedCount}/${ingredientSummary.count}`
                        : ''})
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', fontSize: 13 }}>
                    <div>
                      <span style={{ color: '#6b7280', fontSize: 11, marginRight: 4 }}>{t('중량')}</span>
                      <span style={{ fontWeight: 800, color: '#18181B', fontSize: 15 }}>
                        {ingredientSummary.totalWeight.toLocaleString()}
                      </span>
                      <span style={{ color: '#6b7280', fontSize: 11, marginLeft: 2 }}>{T.qtyUnit}</span>
                      {ingredientSummary.tOut > 0 && ingredientSummary.diffPct !== null && (
                        <span style={{
                          fontSize: 10, marginLeft: 6, padding: '2px 6px', borderRadius: 4,
                          fontWeight: 700,
                          background: Math.abs(ingredientSummary.diffPct) <= 5 ? '#F4F4F5' : '#F4F4F5',
                          color: Math.abs(ingredientSummary.diffPct) <= 5 ? '#EA580C' : '#b91c1c',
                        }}>
                          {t('총생산량 대비')} {ingredientSummary.diffPct >= 0 ? '+' : ''}{ingredientSummary.diffPct}%
                        </span>
                      )}
                    </div>
                    {ingredientSummary.totalCost > 0 && (
                      <div style={{ borderLeft: '1px solid #FAFAFA', paddingLeft: 14 }}>
                        <span style={{ color: '#6b7280', fontSize: 11, marginRight: 4 }}>{t('원가')}</span>
                        <span style={{ fontWeight: 800, color: '#18181B', fontSize: 15 }}>
                          {money(ingredientSummary.totalCost)}
                        </span>
                        {ingredientSummary.tOut > 0 && Number(servingG) > 0 && (
                          <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 6 }}>
                            ({t('1인분 약')} {money(ingredientSummary.totalCost / ingredientSummary.tOut * Number(servingG))})
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {ingredientSummary.matchedCount < ingredientSummary.count && (
                  <div style={{ fontSize: 10, color: '#52525B', marginTop: 6 }}>
                    ⚠ {lang === 'zh'
                      ? `${ingredientSummary.count - ingredientSummary.matchedCount}项未匹配单价，未计入成本`
                      : `단가 미매칭 ${ingredientSummary.count - ingredientSummary.matchedCount}건은 원가에서 제외됨`}
                  </div>
                )}
              </div>
            )}

            <button onClick={addIngredient} type="button"
              style={{
                width: '100%', padding: '14px',
                borderRadius: 10, border: 'none',
                background: '#EA580C', color: '#fff',
                fontSize: 14, fontWeight: 700, cursor: 'pointer', marginTop: 8,
                boxShadow: '0 3px 12px rgba(79,125,92,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
              {t(T.ingredient + ' 추가')}
            </button>

            <div style={{
              marginTop: 18, padding: '14px 16px',
              background: '#f9fafb', borderRadius: 10,
              border: '1px solid #e5e7eb',
            }}>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700, marginBottom: 10 }}>
                📐 {t('분량 정보')}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={labelStyle}>{t(T.totalOutputLabel)}</label>
                  <input type="number" value={totalOutput} onChange={e => setTotalOutput(e.target.value)}
                    placeholder={T.lossRateEnabled ? '예: 10' : (lang === 'zh' ? '例：1000' : '예: 1000')} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>{t(T.servingBaseLabel)}</label>
                  <input type="number" value={servingG} onChange={e => setServingG(e.target.value)}
                    placeholder={T.lossRateEnabled ? '예: 10' : (lang === 'zh' ? '例：150' : '예: 150')} style={inputStyle} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 하단 버튼 */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', gap: '10px',
          position: 'sticky', bottom: 0, background: '#fff',
        }}>
          {!isNew && (
            <button onClick={() => { if (window.confirm(t('정말 삭제하시겠습니까?'))) onDelete(item.id); }}
              style={{
                padding: '11px 20px', borderRadius: '8px', border: '1.5px solid #E4E4E7',
                background: '#fff5f5', color: '#52525B', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
              }}>
              🗑 {t('삭제')}
            </button>
          )}
          <div style={{ display: 'flex', gap: '10px', marginLeft: 'auto' }}>
            <button onClick={onClose}
              style={{
                padding: '11px 20px', borderRadius: '8px', border: '1.5px solid #e5e7eb',
                background: '#fff', color: '#374151', fontSize: '14px', cursor: 'pointer',
              }}>
              {t('취소')}
            </button>
            <button onClick={handleSave}
              style={{
                padding: '11px 24px', borderRadius: '8px', border: 'none',
                background: '#FF6F0F', color: '#fff', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
              }}>
              {t('저장')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RecipeEditor;
